import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import StatTile, { StatTone } from '../charts/StatTile';
import OrderThread from '../../hero/OrderThread';
import SpoolGauge from '../../hero/SpoolGauge';
import { ThreadLensKey, ThreadLensTask, ThreadLensTone } from '../../../interfaces/threadLens.interface';
import { useThreadLens } from './useThreadLens';
import { useLensWorkItems } from './useLensWorkItems';

/** Visible rows in Needs action; the rest collapse into an "N more" note. */
export const MAX_VISIBLE_TASKS = 8;

const TONE: Record<ThreadLensTone, StatTone> = { neutral: 'default', good: 'good', warn: 'warning', bad: 'critical' };
const toneOf = (t?: ThreadLensTone): StatTone => (t ? TONE[t] ?? 'default' : 'default');

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 0, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{title}</Typography>
            {children}
        </Paper>
    );
}

function MaybeLink({ to, children }: { to?: string; children: React.ReactNode }) {
    return to ? <Link component={RouterLink} to={to} underline="hover" color="inherit">{children}</Link> : <>{children}</>;
}

/**
 * The role-aware body of the Thread home: stat row, what needs action, a gauge, the threads and a side
 * list, all from ONE server answer. The server leaves out any section the viewer may not see, so an
 * absent section is simply not drawn (never a zero).
 */
export default function RoleLensBoard({ institutionId, lens, fallback }: { institutionId: string; lens: ThreadLensKey; fallback?: React.ReactNode }) {
    const { data, loading, error, forbidden } = useThreadLens(institutionId, lens);
    const workItems = useLensWorkItems(institutionId, lens);

    if (loading && !data) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="lens-loading">
                <CircularProgress size={24} aria-label="Loading your board" />
            </Box>
        );
    }
    // The caller does not hold this lens's key (for example a saved Thread choice after a role change): draw the Tiles home instead of an empty board.
    if (forbidden && fallback) return <>{fallback}</>;
    if (error || !data) {
        return <Alert severity="error" sx={{ mb: 2 }}>Could not load your board. Reload the page to try again.</Alert>;
    }

    const stats = data.stats ?? [];
    // Role work first (same items and gates as the Tiles Action Center), then the lens's own documents.
    const lensTasks = data.tasks?.items ?? [];
    const hasInboxTask = lensTasks.some(t => t.key === 'approvals');
    const workTasks: ThreadLensTask[] = workItems
        .filter(i => !(hasInboxTask && i.id === 'approvals.waiting'))
        .map(i => ({
            key: `work-${i.id}`,
            title: i.count > 0 ? `${i.count} ${i.title.toLowerCase()}` : i.title,
            detail: i.detail,
            tone: i.severity === 'CRITICAL' ? 'bad' as const : 'warn' as const,
            link: i.link,
        }));
    const allTasks = [...workTasks, ...lensTasks];
    const tasks = data.tasks || workTasks.length > 0
        ? { title: data.tasks?.title ?? 'Needs action', items: allTasks.slice(0, MAX_VISIBLE_TASKS) }
        : undefined;
    const hiddenTasks = Math.max(0, allTasks.length - MAX_VISIBLE_TASKS);
    const rows = data.threads?.rows ?? [];
    const side = data.side;
    const hasAnything = stats.length > 0 || tasks || data.gauge || data.threads || side;

    if (!hasAnything) {
        return <Alert severity="info" sx={{ mb: 2 }}>Nothing to show here yet. Ask an admin to add the view access for your role.</Alert>;
    }

    return (
        <Box sx={{ minWidth: 0, mb: 2 }} data-testid="role-lens-board" data-lens={data.lens}>
            {stats.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1, mb: 2, minWidth: 0 }} data-help-section="dashboard.lens.stats">
                    <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
                        {stats.map(s => (
                            <StatTile key={s.key} label={s.label} value={s.value} sub={s.hint} tone={toneOf(s.tone)} />
                        ))}
                    </Stack>
                </Paper>
            )}

            {(tasks || data.gauge) && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 1.5, mb: 2 }}>
                    {tasks && (
                        <Box sx={{ gridColumn: { xs: 'span 12', md: data.gauge ? 'span 8' : 'span 12' }, minWidth: 0 }} data-help-section="dashboard.lens.tasks">
                            <Section title={tasks.title}>
                                {tasks.items.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">Nothing needs action right now.</Typography>
                                ) : (
                                    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                                        {tasks.items.map(t => (
                                            <Box component="li" key={t.key} data-help="dashboard.lens.taskRow"
                                                 sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                                                <Typography variant="body2" fontWeight={600}
                                                            color={t.tone === 'bad' ? 'error.main' : 'text.primary'} sx={{ overflowWrap: 'anywhere' }}>
                                                    <MaybeLink to={t.link}>{t.title}</MaybeLink>
                                                </Typography>
                                                {t.detail && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{t.detail}</Typography>}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                                {hiddenTasks > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{hiddenTasks} more</Typography>
                                )}
                            </Section>
                        </Box>
                    )}
                    {data.gauge && (
                        <Box sx={{ gridColumn: { xs: 'span 12', md: tasks ? 'span 4' : 'span 12' }, minWidth: 0 }} data-help-section="dashboard.lens.gauge">
                            <Section title={data.gauge.title}>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <SpoolGauge value={data.gauge.percent} label={data.gauge.title} caption={data.gauge.caption} />
                                </Box>
                            </Section>
                        </Box>
                    )}
                </Box>
            )}

            {(data.threads || side) && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 1.5 }}>
                    {data.threads && (
                        <Box sx={{ gridColumn: { xs: 'span 12', lg: side ? 'span 8' : 'span 12' }, minWidth: 0 }} data-help-section="dashboard.lens.threads">
                            <Section title={data.threads.title}>
                                {rows.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">Nothing open.</Typography>
                                ) : (
                                    <Stack spacing={1.5}>
                                        {rows.map(r => (
                                            <Box key={r.id} data-help="dashboard.lens.threadRow" sx={{ minWidth: 0 }}>
                                                <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', flexWrap: 'wrap', mb: 0.5 }}>
                                                    <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                                                        <MaybeLink to={r.link}>{r.title}</MaybeLink>
                                                        {r.subtitle && <Typography component="span" variant="caption" color="text.secondary"> {r.subtitle}</Typography>}
                                                    </Typography>
                                                    {r.amount && <Typography variant="body2">{r.amount}</Typography>}
                                                </Stack>
                                                <OrderThread compact name={r.title} steps={r.steps} />
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Section>
                        </Box>
                    )}
                    {side && (
                        <Box sx={{ gridColumn: { xs: 'span 12', lg: data.threads ? 'span 4' : 'span 12' }, minWidth: 0 }} data-help-section="dashboard.lens.side">
                            <Section title={side.title}>
                                {side.items.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">Nothing listed.</Typography>
                                ) : (
                                    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                                        {side.items.map((i, idx) => (
                                            <Box component="li" key={`${i.title}-${idx}`} data-help="dashboard.lens.sideRow" sx={{ py: 0.5 }}>
                                                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}><MaybeLink to={i.link}>{i.title}</MaybeLink></Typography>
                                                {i.detail && <Typography variant="caption" color="text.secondary">{i.detail}</Typography>}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Section>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}
